/**
 * SYB Cookie Consent
 *
 * Single source of truth is Shopify.customerPrivacy — the merchant's configured regions
 * (Settings > Customer privacy > Cookie banner > Regions) evaluated against the visitor's actual
 * region. The theme holds NO region list of its own; see docs/KNOWN-ISSUES.md for the three ways
 * the old theme-side list was wrong.
 *
 * Two API calls carry the whole decision:
 *   shouldShowBanner()          -> visitor is in a configured region AND has not decided yet
 *   analyticsProcessingAllowed() -> this visitor may be tracked right now
 *
 * The second is true for a non-gated visitor at "no_interaction", true for a gated visitor who
 * accepted, and false for a gated visitor who declined — which is exactly the predicate for
 * "may I load Yotpo/Hotjar/etc". Shopify's guidance is to use shouldShowBanner for initial state
 * rather than deciding regionally in the theme: https://shopify.dev/docs/api/customer-privacy
 *
 * Fires syb:consent:granted / syb:consent:denied.
 */
(function () {
  'use strict';

  // If customerPrivacy never arrives we cannot tell whether this visitor is in a gated region.
  // Fail SAFE (show the banner, withhold trackers) rather than fail open — but recoverable, since
  // the visitor can still accept and unblock themselves.
  var API_TIMEOUT_MS = 8000;

  function getBanner() { return document.getElementById('syb-consent-banner'); }

  function showBanner() {
    var el = getBanner();
    if (!el) return;
    el.hidden = false;
    void el.offsetWidth; // force reflow so the transition fires
    el.classList.add('is-visible');
  }

  function hideBanner() {
    var el = getBanner();
    if (!el) return;
    el.classList.remove('is-visible');
    setTimeout(function () { el.hidden = true; }, 260);
  }

  function loadTrackers() {
    if (typeof window.SYB_LOAD_TRACKERS === 'function') window.SYB_LOAD_TRACKERS();
  }

  function fire(name) {
    try {
      document.dispatchEvent(new CustomEvent(name));
    } catch (e) {
      var evt = document.createEvent('Event');
      evt.initEvent(name, true, true);
      document.dispatchEvent(evt);
    }
  }

  function setConsent(granted) {
    var payload = {
      analytics: granted,
      marketing: granted,
      preferences: granted,
      sale_of_data: granted
    };
    var done = function () {
      fire(granted ? 'syb:consent:granted' : 'syb:consent:denied');
      hideBanner();
    };
    try {
      window.Shopify.customerPrivacy.setTrackingConsent(payload, done);
    } catch (e) {
      done();
    }
  }

  function wireButtons() {
    var el = getBanner();
    if (!el) return;
    el.addEventListener('click', function (ev) {
      var btn = ev.target.closest('[data-consent]');
      if (!btn) return;
      setConsent(btn.getAttribute('data-consent') === 'accept');
    });
  }

  function decide() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;

    if (!cp || typeof cp.shouldShowBanner !== 'function') {
      // Region unknown. Withhold trackers and let the visitor decide for themselves.
      window.SYB_CONSENT_STATE = 'api-unavailable';
      showBanner();
      return;
    }

    var needsDecision = false;
    var mayTrack = false;
    try { needsDecision = cp.shouldShowBanner(); } catch (e) { needsDecision = true; }
    try { mayTrack = cp.analyticsProcessingAllowed(); } catch (e) { mayTrack = false; }

    if (needsDecision) {
      // Gated region, no decision recorded yet. Ask, and load nothing until they answer.
      window.SYB_CONSENT_STATE = 'awaiting-decision';
      showBanner();
      return;
    }

    // No banner needed: either this region is not gated, or the visitor already decided.
    // analyticsProcessingAllowed() distinguishes "not gated / accepted" from "declined".
    window.SYB_CONSENT_STATE = mayTrack ? 'allowed' : 'declined';
    if (mayTrack) loadTrackers();
  }

  function init() {
    wireButtons();
    decide();
  }

  function bootstrap() {
    if (window.Shopify && window.Shopify.customerPrivacy) {
      init();
      return;
    }
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      var settled = false;
      var timer = setTimeout(function () {
        if (settled) return;
        settled = true;
        init(); // decide() will hit the api-unavailable branch and fail safe
      }, API_TIMEOUT_MS);
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        function () {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          init();
        }
      );
      return;
    }
    init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  // Accepting mid-session unblocks the gated trackers.
  document.addEventListener('syb:consent:granted', loadTrackers);
})();
